// ============================================================
//  src/pages/auth/AuthShell.js
//  Shared split-screen layout for Login / Register / Forgot password.
//  Outputs the same class names as VerifyEmailPage & AgentLoginPage
//  (auth-page / auth-left / auth-right / auth-shell …) so every auth
//  screen shares one look from auth.css.
// ============================================================
import React from 'react';
import { Link } from 'react-router-dom';
import './auth.css';

export function AuthLogo({ to = '/' }) {
  return (
    <Link to={to} className="auth-brand" aria-label="CommandKit home">
      <span className="auth-brand-dot" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="m7 8-4 4 4 4M17 8l4 4-4 4M14 5l-4 14" />
        </svg>
      </span>
      <span>CommandKit</span>
    </Link>
  );
}

// ── Left-panel visuals ───────────────────────────────────────
function ChatVisual() {
  return (
    <div className="auth-visual" aria-hidden="true">
      <div className="av-card">
        <div className="av-head">
          <span className="av-avatar">D</span>
          <div><b>DataHub NG</b><span>Business account · online</span></div>
        </div>
        <div className="av-body">
          <div className="av-msg av-in">Hi, I want to buy data</div>
          <div className="av-msg av-out">
            Welcome back 👋 Pick a plan:
            <div className="av-btns"><span>1GB · ₦300</span><span>2GB · ₦500</span></div>
          </div>
          <div className="av-msg av-in">2GB · ₦500</div>
          <div className="av-msg av-out">Done ✅ 2GB sent. Ref: CK-88213</div>
        </div>
      </div>
      <div className="av-toast">
        <span className="av-toast-ic"><i className="fas fa-check" /></span>
        <div><b>Payment confirmed</b><span>₦500 · just now</span></div>
      </div>
    </div>
  );
}

function SecureVisual() {
  return (
    <div className="auth-visual" aria-hidden="true">
      <div className="av-card av-card-secure">
        <div className="av-lock"><i className="fas fa-shield-halved" /></div>
        <b className="av-secure-title">Your reset code</b>
        <div className="av-code">
          {['4', '8', '2', '9', '1', '7'].map((d, i) => <span key={i}>{d}</span>)}
        </div>
        <span className="av-secure-note"><i className="fas fa-clock" /> Expires in 15 minutes</span>
      </div>
    </div>
  );
}

/**
 * @param {object}  props
 * @param {node}    props.headline   left-panel headline (can contain <em>)
 * @param {string}  props.sub        left-panel supporting copy
 * @param {array}   props.points     left-panel bullet points
 * @param {string}  props.visual     'chat' | 'secure'
 * @param {node}    props.switchText e.g. "New to CommandKit?"
 * @param {string}  props.switchTo   route for the top-right link
 * @param {string}  props.switchLabel
 * @param {string}  props.title      form heading
 * @param {node}    props.subtitle   form sub-heading
 * @param {node}    props.above      optional element above the heading (e.g. step indicator)
 */
export default function AuthShell({
  headline, sub, points = [], visual = 'chat',
  switchText, switchTo, switchLabel,
  title, subtitle, above, children,
}) {
  return (
    <div className="auth-page">
      <aside className="auth-left">
        <div className="auth-left-main">
          <AuthLogo />
          <div className="auth-left-copy">
            <h1 className="auth-headline">{headline}</h1>
            {sub && <p className="auth-sub">{sub}</p>}
            {points.length > 0 && (
              <ul className="auth-features">
                {points.map(p => <li key={p}><i className="fas fa-check" /> {p}</li>)}
              </ul>
            )}
          </div>
          {visual === 'secure' ? <SecureVisual /> : <ChatVisual />}
          <div className="auth-footer-badge">
            <i className="fas fa-shield-halved" /> Built on the official WhatsApp Business Platform
          </div>
        </div>
      </aside>

      <main className="auth-right">
        <header className="auth-topbar">
          <span className="auth-topbar-brand"><AuthLogo /></span>
          {switchTo && (
            <span className="auth-switch">
              <span className="auth-switch-text">{switchText}</span>{' '}
              <Link to={switchTo}>{switchLabel}</Link>
            </span>
          )}
        </header>

        <div className="auth-shell">
          {above}
          <div className="auth-hdr">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {children}
        </div>

        <footer className="auth-legal">
          <span>© {new Date().getFullYear()} CommandKit</span>
          <span className="auth-legal-dot" />
          <span><i className="fas fa-lock" /> Secured with 256-bit SSL</span>
        </footer>
      </main>
    </div>
  );
}

// ── Small shared pieces ──────────────────────────────────────
export function AuthAlert({ children, type = 'error' }) {
  if (!children) return null;
  return (
    <div className={`auth-alert auth-alert-${type}`} role="alert">
      <i className={`fas ${type === 'error' ? 'fa-circle-exclamation' : 'fa-circle-check'}`} />
      <span>{children}</span>
    </div>
  );
}

export function passwordScore(pw = '') {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 6) s += 1;
  if (pw.length >= 10) s += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s += 1;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s += 1;
  else if (/\d/.test(pw) || /[^A-Za-z0-9]/.test(pw)) s += 0.5;
  return Math.min(4, Math.max(1, Math.floor(s)));
}

export function PasswordStrength({ value }) {
  if (!value) return null;
  const score = value.length < 6 ? 0 : passwordScore(value);
  const labels = ['Too short', 'Weak', 'Okay', 'Good', 'Strong'];
  return (
    <div className={`pw-meter pw-${score}`} aria-live="polite">
      <div className="pw-bars">{[1, 2, 3, 4].map(i => <span key={i} className={i <= score ? 'on' : ''} />)}</div>
      <span className="pw-label">{labels[score]}</span>
    </div>
  );
}
